// Stock for one variant in the other stores of this business unit's network, and
// the transfer request that pulls it here. Same services the Retail layout's
// stock drawer uses (getProductsNearbyStock / generateTransferRequest), without
// its placeholder "current store" row: the cashier already sees this store's
// stock in the size picker, so only other warehouses are listed.

export const NEARBY_STOCK_QUERY = `
  query NearbyStock($bUnitId: String, $code: String) {
    getProductsNearbyStock(bUnitId: $bUnitId, code: $code) {
      csBunitId
      totalQty
      stores {
        csNearbyStoreId
        name
        nearbybUnitId
        viewStock
        isIssue
        warehouse {
          mWarehouseID
          name
          storageBin {
            totalQty
            stocks {
              qtyonhand
            }
          }
        }
      }
    }
  }
`;

export function buildNearbyStockRequest(tillData, code) {
  return { query: NEARBY_STOCK_QUERY, variables: { bUnitId: tillData?.tillAccess?.csBunit?.csBunitId, code: String(code || "") } };
}

const binQty = (bin) => {
  if (!bin) return 0;
  const total = Number(bin.totalQty);
  if (Number.isFinite(total)) return total;
  return (bin.stocks || []).reduce((n, s) => n + (Number(s?.qtyonhand) || 0), 0);
};

// Response → [{ bunitId, name, warehouseId, qty, canIssue }], other stores only,
// most stock first. Stores that don't share their stock (viewStock "N") are left out.
export function parseNearbyStores(response, tillData) {
  const stores = response?.data?.data?.getProductsNearbyStock?.stores;
  if (!Array.isArray(stores)) return null;
  const here = tillData?.tillAccess?.csBunit;
  return stores
    .filter((s) => s && s.viewStock !== "N")
    .filter((s) => s.warehouse?.mWarehouseID && s.warehouse.mWarehouseID !== here?.mWarehouse?.mWarehouseID && s.nearbybUnitId !== here?.csBunitId)
    .map((s) => ({
      bunitId: s.nearbybUnitId,
      name: s.name || s.warehouse.name || "Store",
      warehouseId: s.warehouse.mWarehouseID,
      qty: Math.max(0, binQty(s.warehouse.storageBin)),
      canIssue: s.isIssue === "Y",
    }))
    .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
}

// The dataLoad argument is JSON inside a GraphQL string literal, so backslashes
// must be escaped before quotes (the Retail builder only escapes quotes).
export const toGraphQLString = (value) => JSON.stringify(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

export function buildTransferRequestMutation({ fromBunitId, toBunitId, lines, userId, description = "", now = new Date() }) {
  const stamp = now.toISOString().replace("T", " ").replace("Z", "");
  const dataLoad = [
    {
      fromBunitId,
      toBunitId,
      created: stamp,
      createdby: userId,
      updated: stamp,
      updatedby: userId,
      requiredDate: now.toISOString().split("T")[0],
      requestFor: "ST",
      description,
      lines: lines.map((l) => ({ productId: l.productId, qtyRequested: l.qty, pRequisitionLineId: null })),
    },
  ];
  return `mutation { generateTransferRequest(dataLoad: "${toGraphQLString(dataLoad)}") { message code recordId } }`;
}

// Returns an error message, or "" when the request may be sent.
export function transferQtyError(qty, store) {
  const n = Number(qty);
  if (!Number.isInteger(n) || n < 1) return "Enter a whole number of 1 or more.";
  if (!store?.canIssue) return `${store?.name || "This store"} can't send stock.`;
  if (n > store.qty) return `${store.name} has only ${store.qty}.`;
  return "";
}

export function readTransferResult(response) {
  const r = response?.data?.data?.generateTransferRequest;
  if (r && String(r.code) === "200") return { ok: true, recordId: r.recordId || "", message: r.message || "" };
  const err = response?.data?.errors?.[0]?.message;
  return { ok: false, message: r?.message || err || "The request didn't reach the server. Check the connection and try again." };
}

// Store × size grid for one colour: rows of { store, cells: { [size]: qty } }.
export function buildStockGrid(perSize) {
  const byStore = new Map();
  for (const { size, stores } of perSize) {
    for (const s of stores || []) {
      if (!byStore.has(s.bunitId)) byStore.set(s.bunitId, { store: { ...s, qty: 0 }, cells: {}, stores: {} });
      const row = byStore.get(s.bunitId);
      row.cells[size] = s.qty;
      row.stores[size] = s;
      row.store.qty += s.qty;
    }
  }
  return [...byStore.values()].sort((a, b) => b.store.qty - a.store.qty || a.store.name.localeCompare(b.store.name));
}

// Last-synced on-hand quantity for this store, or null when the till doesn't track it.
export function localStockLabel(product) {
  const raw = product?.onhandQty;
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n > 0 ? `${n} here` : "Out here";
}

// Pieces already promised in each store: open holds and accepted online orders
// (getFashionStockCommitments). Free stock is on hand less both.
const cleanId = (s) => String(s ?? "").replace(/["\\]/g, "");
export function buildStockCommitmentsQuery(productIds) {
  const ids = [...new Set((productIds || []).filter(Boolean).map(cleanId))];
  return { query: `query { getFashionStockCommitments(productIds: [${ids.map((id) => `"${id}"`).join(", ")}]) { bunitId productId held reserved } }` };
}

// Response → { "bunitId|productId": { held, reserved } }, or null when the call failed.
export function readStockCommitments(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionStockCommitments;
  if (!Array.isArray(rows)) return null;
  const out = {};
  for (const r of rows) {
    if (!r?.bunitId || !r?.productId) continue;
    out[`${r.bunitId}|${r.productId}`] = { held: Math.max(0, Number(r.held) || 0), reserved: Math.max(0, Number(r.reserved) || 0) };
  }
  return out;
}

// On hand at a store less what is promised there; never below zero.
export function freeStock(onHand, commitments, bunitId, productId) {
  const c = commitments?.[`${bunitId}|${productId}`] || { held: 0, reserved: 0 };
  const promised = c.held + c.reserved;
  return { onHand, free: Math.max(0, onHand - promised), promised };
}
